import { BasicServiceConnection, SubscriberFunctionInterface } from '@App/sockets/basic_connection'
import * as types from '@App/sockets/types'
import { IEventParams, ISubscribeParams } from '@App/sockets/types'

import { generateRequestKey } from './helpers'
const CurrencyServiceDestination = 'go.currency_service'

class PublicServiceConnection extends BasicServiceConnection {
  public name: string = types.ServiceNames.PublicService
  public path: string = ''
  private activeSubscriptionsCount = new Map<string, number>()
  getRateSocketMessage(
    source_currency: string,
    target_currency: string,
    request_type: string,
    component?: string,
  ): string {
    const key = `${types.Events.GetRate}${source_currency}${target_currency}`

    if (request_type === types.RequestTypes.Subscribe) {
      const val = this.activeSubscriptionsCount.get(key) ?? 0
      this.activeSubscriptionsCount.set(key, val + 1)
      if (val > 0) {
        return ''
      }
    }
    if (request_type === types.RequestTypes.Unsubscribe) {
      const val = this.activeSubscriptionsCount.get(key) ?? 0
      if (!val) return ''
      const newVal = val - 1
      this.activeSubscriptionsCount.set(key, newVal)

      if (newVal > 0) return ''
    }

    return JSON.stringify({
      destination: CurrencyServiceDestination,
      request_type: request_type,
      event: types.Events.GetRate,
      request_key: generateRequestKey(types.Events.GetRate),
      payload: {
        source_currency,
        target_currency,
      },
    })
  }

  getTickerSocketMessage(request_type = types.RequestTypes.Subscribe, period: string = '24'): string {
    return JSON.stringify({
      destination: CurrencyServiceDestination,
      request_type: request_type,
      event: types.Events.GetTicker,
      request_key: generateRequestKey(types.Events.GetTicker),
      payload: { period },
    })
  }

  getOrderBookSocketMessage(source_currency: string, target_currency: string, request_type: string): string {
    return JSON.stringify({
      destination: CurrencyServiceDestination,
      request_type: request_type,
      event: types.Events.GetOrderBook,
      request_key: generateRequestKey(types.Events.GetOrderBook),
      payload: { source_currency, target_currency },
    })
  }

  getTradesSocketMessage(source_currency: string, target_currency: string, request_type: string): string {
    return JSON.stringify({
      destination: CurrencyServiceDestination,
      request_type: request_type,
      event: types.Events.GetTrades,
      request_key: generateRequestKey(types.Events.GetTrades),
      payload: { source_currency, target_currency },
    })
  }

  getMarkets(request_type: string) {
    return JSON.stringify({
      destination: CurrencyServiceDestination,
      request_type: request_type,
      event: types.Events.GetMarkets,
      request_key: generateRequestKey(types.Events.GetMarkets),
      payload: {},
    })
  }

  // TODO: Remove all subscriptions, divide one by one
  subscribe(params: ISubscribeParams, event: string, func: SubscriberFunctionInterface): void {
    try {
      if (!this.getConnection()) {
        this.connection = this.establishConnection(func)
      }

      this.connected().then(() => {
        switch (event) {
          case types.Events.GetRate: {
            const { source_currency, target_currency, component } = params as IEventParams[types.Events.GetRate]
            this.getConnection().send(
              this.getRateSocketMessage(source_currency, target_currency, types.RequestTypes.Subscribe, component),
            )
            break
          }
          case types.Events.GetOrderBook: {
            const { source_currency, target_currency } = params as IEventParams[types.Events.GetOrderBook]
            this.getConnection().send(
              this.getOrderBookSocketMessage(source_currency, target_currency, types.RequestTypes.Subscribe),
            )
            break
          }
          case types.Events.GetTicker: {
            const { period = '24' } = params as IEventParams[types.Events.GetTicker]
            this.getConnection().send(this.getTickerSocketMessage(types.RequestTypes.Subscribe, period))
            break
          }
          case types.Events.GetTrades: {
            const { source_currency, target_currency } = params as IEventParams[types.Events.GetTrades]
            this.getConnection().send(
              this.getTradesSocketMessage(source_currency, target_currency, types.RequestTypes.Subscribe),
            )
            break
          }
          case types.Events.GetMarkets: {
            this.getConnection().send(
              this.getMarkets(types.RequestTypes.Subscribe)
            )
            break
          }
        }
      })
    } catch (error) {
      console.log(error)
    }
  }

  unsubscribe(params: ISubscribeParams, event: string) {
    if (this.connection) {
      switch (event) {
        case types.Events.GetRate: {
          const { source_currency, target_currency, component } = params as IEventParams[types.Events.GetRate]

          this.connected().then(() => {
            this.getConnection().send(
              this.getRateSocketMessage(source_currency, target_currency, types.RequestTypes.Unsubscribe, component),
            )
          })
          break
        }
        case types.Events.GetOrderBook: {
          const { source_currency, target_currency } = params as IEventParams[types.Events.GetOrderBook]
          this.connected().then(() => {
            this.getConnection().send(
              this.getOrderBookSocketMessage(source_currency, target_currency, types.RequestTypes.Unsubscribe),
            )
          })
          break
        }
        case types.Events.GetTicker: {
          const { period = '24' } = params as IEventParams[types.Events.GetTicker]
          this.connected().then(() => {
            this.getConnection().send(this.getTickerSocketMessage(types.RequestTypes.Unsubscribe, period))
          })
          break
        }
        case types.Events.GetTrades: {
          const { source_currency, target_currency } = params as IEventParams[types.Events.GetTrades]
          this.connected().then(() => {
            this.getConnection().send(
              this.getTradesSocketMessage(source_currency, target_currency, types.RequestTypes.Unsubscribe),
            )
          })
          break
        }
        case types.Events.GetMarkets: {
          this.connected().then(() => {
            this.getConnection().send(
              this.getMarkets(types.RequestTypes.Unsubscribe)
            )
          })
          break
        }
      }
    }
  }
}
export default new PublicServiceConnection()
